import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import trLocale from '@fullcalendar/core/locales/tr';
import {
  Card,
  Modal,
  Button,
  Form,
  Input,
  Select,
  message,
  Descriptions,
  Space,
  Tag,
  Switch,
  DatePicker,
  TimePicker,
} from 'antd';
import {
  CalendarOutlined,
  PlusOutlined,
  FileTextOutlined,
  UserOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchCalendarEvents,
  fetchAppointment,
  createAppointment,
  updateAppointment,
  updateAppointmentStatus,
  convertToServiceOrder,
  checkTechnicianConflicts,
  type CalendarEventDto,
  type ServiceAppointmentDto,
} from '../services/appointmentService';
import {
  fetchServiceOrder,
  type ServiceOrderDto,
} from '../services/serviceOrderService';
import {
  fetchCustomerVehicles,
  createCustomerVehicle,
  type CustomerVehicleDto,
} from '../services/serviceOrderService';
import { api } from '../services/api';
import { fetchPersonnelList, type PersonnelListItem } from '../services/personnelService';
import AccountSelectModal from '../components/AccountSelectModal';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';
import type { EventClickArg } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';

const { TextArea } = Input;

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
}

const Ajanda: React.FC = () => {
  const navigate = useNavigate();
  const [calendarRef, setCalendarRef] = useState<FullCalendar | null>(null);
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [showClosedAppointments, setShowClosedAppointments] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailEvent, setDetailEvent] = useState<CalendarEventDto | null>(null);
  const [serviceOrderDetail, setServiceOrderDetail] = useState<ServiceOrderDto | null>(null);
  const [appointmentDetail, setAppointmentDetail] = useState<ServiceAppointmentDto | null>(null);
  const [appointmentModalVisible, setAppointmentModalVisible] = useState(false);
  const [appointmentForm] = Form.useForm();
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [vehicleForm] = Form.useForm();
  const [convertModalVisible, setConvertModalVisible] = useState(false);
  const [convertForm] = Form.useForm();
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [vehicles, setVehicles] = useState<CustomerVehicleDto[]>([]);
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [users, setUsers] = useState<PersonnelListItem[]>([]);
  const [editingAppointmentId, setEditingAppointmentId] = useState<number | null>(null);

  const loadEvents = useCallback(async (start: Date, end: Date) => {
    setLoading(true);
    try {
      const data = await fetchCalendarEvents(start, end);
      setAllEvents(data.map((e: any) => ({
        ...e,
        start: e.start,
        end: e.end,
      })));
    } catch (err) {
      message.error('Takvim yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const events = React.useMemo(() => {
    if (showClosedAppointments) return allEvents;
    return allEvents.filter((e: any) => {
      const ext = e.extendedProps || {};
      if (ext.type === 'appointment' && (ext.status === 3 || ext.status === 4)) return false;
      return true;
    });
  }, [allEvents, showClosedAppointments]);

  useEffect(() => {
    loadBrands();
    loadUsers();
  }, []);

  const loadBrands = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch {
      console.error('Markalar yüklenemedi');
    }
  };

  const loadModelsForBrand = async (brandId: number) => {
    try {
      const data = await fetchModels(brandId);
      setModels(data);
    } catch {
      console.error('Modeller yüklenemedi');
    }
  };

  const loadUsers = async () => {
    try {
      const data = await fetchPersonnelList();
      setUsers(data);
    } catch {
      console.error('Personel listesi yüklenemedi');
    }
  };

  const handleDatesSet = (arg: { start: Date; end: Date }) => {
    loadEvents(arg.start, arg.end);
  };

  const handleDateClick = (arg: DateClickArg) => {
    setSelectedDate(dayjs(arg.date));
    setSelectedAccount(null);
    setEditingAppointmentId(null);
    appointmentForm.resetFields();
    appointmentForm.setFieldsValue({
      date: dayjs(arg.date),
      time: dayjs(arg.date).format('HH:mm') ? dayjs(arg.date) : dayjs().hour(9).minute(0),
    });
    setAppointmentModalVisible(true);
  };

  const handleEventClick = async (arg: EventClickArg) => {
    arg.jsEvent.preventDefault();
    const event = arg.event;
    const ext = event.extendedProps as any;
    setDetailEvent({
      id: event.id,
      title: event.title,
      start: event.start?.toISOString() ?? '',
      end: event.end?.toISOString() ?? '',
      extendedProps: ext,
    });

    if (ext?.type === 'serviceOrder' && ext.serviceOrderId) {
      try {
        const so = await fetchServiceOrder(ext.serviceOrderId);
        setServiceOrderDetail(so);
        setAppointmentDetail(null);
      } catch {
        message.error('Servis fişi yüklenemedi');
      }
    } else if (ext?.type === 'appointment' && ext.appointmentId) {
      try {
        const apt = await fetchAppointment(ext.appointmentId);
        setAppointmentDetail(apt);
        setServiceOrderDetail(null);
      } catch {
        message.error('Randevu yüklenemedi');
      }
    }
    setDetailModalVisible(true);
  };

  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account);
    setAccountModalVisible(false);
    loadVehiclesForCustomer(account.id);
    appointmentForm.setFieldsValue({ customerId: account.id });
  };

  const loadVehiclesForCustomer = async (customerId: number) => {
    try {
      const data = await fetchCustomerVehicles({ customerId });
      setVehicles(data);
    } catch {
      setVehicles([]);
    }
  };

  const handleSaveAppointment = async () => {
    try {
      const values = await appointmentForm.validateFields();
      if (!selectedAccount) {
        message.error('Lütfen müşteri seçin');
        return;
      }

      const appointmentDate = values.date
        ? dayjs(values.date).hour(values.time?.hour() ?? 9).minute(values.time?.minute() ?? 0).toISOString()
        : (selectedDate?.hour(9).minute(0).toISOString() ?? '');

      if (editingAppointmentId) {
        await updateAppointment(editingAppointmentId, {
          vehicleId: values.vehicleId ?? null,
          appointmentDate,
          status: values.status ?? 1,
          notes: values.notes ?? null,
        });
        message.success('Randevu güncellendi');
      } else {
        const { smsSent } = await createAppointment({
          customerId: selectedAccount.id,
          vehicleId: values.vehicleId ?? null,
          appointmentDate,
          notes: values.notes ?? null,
        });
        message.success(smsSent ? 'Randevu oluşturuldu. SMS gönderildi.' : 'Randevu oluşturuldu.');
      }

      setAppointmentModalVisible(false);
      setEditingAppointmentId(null);
      if (calendarRef) {
        const view = calendarRef.getApi().view;
        if (view) loadEvents(view.activeStart, view.activeEnd);
      }
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Randevu kaydedilemedi');
    }
  };

  const handleAddVehicle = () => {
    if (!selectedAccount) {
      message.warning('Önce müşteri seçin');
      return;
    }
    vehicleForm.resetFields();
    vehicleForm.setFieldsValue({ currentKm: 0 });
    setVehicleModalVisible(true);
  };

  const handleSaveVehicle = async () => {
    try {
      await vehicleForm.validateFields();
      if (!selectedAccount) return;
      const values = vehicleForm.getFieldsValue();
      await createCustomerVehicle({
        customerId: selectedAccount.id,
        plate: values.plate,
        vinChassis: values.vinChassis,
        brandId: values.brandId,
        modelId: values.modelId,
        make: values.make,
        model: values.model,
        year: values.year,
        color: values.color,
        engineNo: values.engineNo,
        currentKm: values.currentKm || 0,
        notes: values.notes,
      });
      message.success('Araç eklendi');
      setVehicleModalVisible(false);
      loadVehiclesForCustomer(selectedAccount.id);
    } catch (err: any) {
      if (err.errorFields) return;
      message.error('Araç eklenemedi');
    }
  };

  const handleStatusChange = async (appointmentId: number, status: number) => {
    try {
      await updateAppointmentStatus(appointmentId, status);
      message.success('Durum güncellendi');
      setDetailModalVisible(false);
      if (calendarRef) {
        const view = calendarRef.getApi().view;
        if (view) loadEvents(view.activeStart, view.activeEnd);
      }
    } catch {
      message.error('Durum güncellenemedi');
    }
  };

  const handleConvertToServiceOrder = () => {
    if (!appointmentDetail) return;
    if (!appointmentDetail.vehicleId) {
      loadVehiclesForCustomer(appointmentDetail.customerId);
      setConvertModalVisible(true);
      convertForm.resetFields();
      convertForm.setFieldsValue({ complaintNotes: appointmentDetail.notes });
    } else {
      doConvert(appointmentDetail.vehicleId, undefined, appointmentDetail.notes ?? undefined);
    }
  };

  const doConvert = async (vehicleId?: number, responsibleUserId?: number, complaintNotes?: string) => {
    if (!appointmentDetail) return;
    if (!vehicleId && !appointmentDetail.vehicleId) {
      message.error('Araç seçimi zorunludur');
      return;
    }
    try {
      const { serviceOrderId } = await convertToServiceOrder(appointmentDetail.id, {
        vehicleId: vehicleId ?? appointmentDetail.vehicleId ?? null,
        responsibleUserId: responsibleUserId ?? null,
        complaintNotes: complaintNotes ?? null,
      });
      message.success('Servis fişi oluşturuldu');
      setConvertModalVisible(false);
      setDetailModalVisible(false);
      navigate(`/service-orders/${serviceOrderId}`);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Servis fişi oluşturulamadı');
    }
  };

  const handleConvertSubmit = async () => {
    try {
      const values = await convertForm.validateFields();
      await doConvert(values.vehicleId, values.responsibleUserId, values.complaintNotes);
    } catch (err: any) {
      if (err.errorFields) return;
    }
  };

  const handleEditAppointment = () => {
    if (!appointmentDetail) return;
    setEditingAppointmentId(appointmentDetail.id);
    setAppointmentModalVisible(false);
    setDetailModalVisible(false);
    api.get(`/accounts/${appointmentDetail.customerId}`).then(({ data }) => {
      setSelectedAccount(data);
      loadVehiclesForCustomer(appointmentDetail.customerId);
      appointmentForm.setFieldsValue({
        customerId: appointmentDetail.customerId,
        vehicleId: appointmentDetail.vehicleId,
        date: dayjs(appointmentDetail.appointmentDate),
        time: dayjs(appointmentDetail.appointmentDate),
        status: appointmentDetail.status,
        notes: appointmentDetail.notes,
      });
      setAppointmentModalVisible(true);
    });
  };

  return (
    <div className="ajanda-page" style={{ padding: '16px', maxWidth: '100%' }}>
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <span>Ajanda</span>
          </Space>
        }
        extra={
          <Space>
            <span style={{ fontSize: 13 }}>Tamamlanan ve iptal edilen randevuları göster</span>
            <Switch
              checked={showClosedAppointments}
              onChange={setShowClosedAppointments}
              checkedChildren="Açık"
              unCheckedChildren="Kapalı"
            />
          </Space>
        }
      >
        <div className="calendar-wrapper" style={{ minHeight: 500, overflowX: 'auto' }}>
          <FullCalendar
            ref={(ref) => setCalendarRef(ref ?? null)}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            locale={trLocale}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={false}
            events={events}
            datesSet={handleDatesSet}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventDisplay="block"
            height="auto"
            buttonText={{
              today: 'Bugün',
              month: 'Ay',
              week: 'Hafta',
              day: 'Gün',
            }}
          />
        </div>
      </Card>

      {/* Event/Detail Modal */}
      <Modal
        title={detailEvent?.extendedProps?.type === 'serviceOrder' ? 'Servis Fişi Detayı' : 'Randevu Detayı'}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          detailEvent?.extendedProps?.type === 'serviceOrder' ? (
            <Button type="primary" icon={<FileTextOutlined />} onClick={() => {
              const id = detailEvent?.extendedProps?.serviceOrderId;
              if (id) navigate(`/service-orders/${id}`);
            }}>
              Servis Fişine Git
            </Button>
          ) : appointmentDetail ? (
            <Space>
              {((appointmentDetail.status === 2) || (appointmentDetail.status === 3)) && !appointmentDetail.serviceOrderId && (
                <Button type="primary" icon={<FileTextOutlined />} onClick={handleConvertToServiceOrder}>
                  Servis Fişi Oluştur
                </Button>
              )}
              {appointmentDetail.status !== 3 && appointmentDetail.status !== 4 && (
                <>
                  <Button onClick={() => handleEditAppointment()}>Düzenle</Button>
                  <Button onClick={() => handleStatusChange(appointmentDetail.id, 3)}>Gerçekleşti</Button>
                  <Button danger onClick={() => handleStatusChange(appointmentDetail.id, 4)}>İptal</Button>
                </>
              )}
            </Space>
          ) : null
        }
        width={560}
      >
        {serviceOrderDetail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Fiş No">{serviceOrderDetail.orderNo}</Descriptions.Item>
            <Descriptions.Item label="Plaka">{serviceOrderDetail.vehiclePlate}</Descriptions.Item>
            <Descriptions.Item label="Müşteri">{serviceOrderDetail.customerName}</Descriptions.Item>
            <Descriptions.Item label="Şikayet/Not">{serviceOrderDetail.complaintNotes || '-'}</Descriptions.Item>
            <Descriptions.Item label="Teknisyen">
              {serviceOrderDetail.responsibleUserName || serviceOrderDetail.lines?.map((l: any) => l.technicianUserName).filter(Boolean).join(', ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Yapılan İşler">
              {serviceOrderDetail.lines?.map((l: any) => `${l.serviceItemName || l.productName || l.externalServiceItemName || '-'}`).join(', ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={
                serviceOrderDetail.status === 1 ? 'green' :
                serviceOrderDetail.status === 2 ? 'blue' :
                serviceOrderDetail.status === 3 ? 'gold' :
                serviceOrderDetail.status === 4 ? 'default' : 'red'
              }>
                {serviceOrderDetail.statusText}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
        {appointmentDetail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Müşteri">{appointmentDetail.customerName}</Descriptions.Item>
            <Descriptions.Item label="Telefon">{appointmentDetail.customerPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Plaka">{appointmentDetail.vehiclePlate || '-'}</Descriptions.Item>
            <Descriptions.Item label="Araç">{appointmentDetail.vehicleInfo || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tarih/Saat">
              {dayjs(appointmentDetail.appointmentDate).format('DD.MM.YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Not">{appointmentDetail.notes || '-'}</Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={
                appointmentDetail.status === 1 ? 'blue' :
                appointmentDetail.status === 2 ? 'green' :
                appointmentDetail.status === 3 ? 'default' : 'red'
              }>
                {appointmentDetail.statusText}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Randevu Oluştur/Düzenle Modal */}
      <Modal
        title={editingAppointmentId ? 'Randevu Düzenle' : 'Yeni Randevu'}
        open={appointmentModalVisible}
        onOk={handleSaveAppointment}
        onCancel={() => {
          setAppointmentModalVisible(false);
          setEditingAppointmentId(null);
          setSelectedAccount(null);
        }}
        okText="Kaydet"
        width={480}
      >
        <Form form={appointmentForm} layout="vertical">
          <Form.Item label="Müşteri" required>
            <Input
              readOnly
              placeholder="Müşteri seçin"
              value={
                selectedAccount
                  ? [selectedAccount.accountName, selectedAccount.companyName].filter(Boolean).join(' - ')
                  : ''
              }
              addonBefore={<UserOutlined />}
              onClick={() => setAccountModalVisible(true)}
              style={{ cursor: 'pointer' }}
            />
          </Form.Item>
          <Form.Item label="Araç (opsiyonel)" name="vehicleId">
            <Select
              placeholder="Araç seçin veya boş bırakın"
              allowClear
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Button type="link" icon={<PlusOutlined />} onClick={handleAddVehicle} block>
                    Yeni araç ekle
                  </Button>
                </>
              )}
            >
              {vehicles.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {v.plate} {v.make && v.model ? `- ${v.make} ${v.model}` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Tarih" name="date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item label="Saat" name="time" rules={[{ required: true }]}>
            <TimePicker style={{ width: '100%' }} format="HH:mm" />
          </Form.Item>
          {editingAppointmentId && (
            <Form.Item label="Durum" name="status">
              <Select>
                <Select.Option value={1}>Beklemede</Select.Option>
                <Select.Option value={2}>Onaylandı</Select.Option>
                <Select.Option value={3}>Gerçekleşti</Select.Option>
                <Select.Option value={4}>İptal</Select.Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item label="Not" name="notes">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Araç Ekle Modal */}
      <Modal
        title="Yeni Araç Ekle"
        open={vehicleModalVisible}
        onOk={handleSaveVehicle}
        onCancel={() => setVehicleModalVisible(false)}
        okText="Kaydet"
      >
        <Form form={vehicleForm} layout="vertical">
          <Form.Item name="plate" label="Plaka" rules={[{ required: true }]}>
            <Input placeholder="34 ABC 123" />
          </Form.Item>
          <Form.Item name="brandId" label="Marka">
            <Select
              placeholder="Marka seçin"
              allowClear
              onChange={(id) => {
                vehicleForm.setFieldsValue({ modelId: undefined });
                if (id) loadModelsForBrand(id);
              }}
            >
              {brands.map((b) => (
                <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="modelId" label="Model">
            <Select placeholder="Model seçin" allowClear>
              {models.map((m) => (
                <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="year" label="Yıl">
            <Input type="number" placeholder="2020" />
          </Form.Item>
          <Form.Item name="currentKm" label="Km">
            <Input type="number" placeholder="0" />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Araç seçerek servis fişi oluştur (randevuda araç yoksa) */}
      <Modal
        title="Servis Fişi Oluştur - Araç Seçin"
        open={convertModalVisible}
        onOk={handleConvertSubmit}
        onCancel={() => setConvertModalVisible(false)}
        okText="Oluştur"
      >
        <Form form={convertForm} layout="vertical">
          <Form.Item name="vehicleId" label="Araç" rules={[{ required: true, message: 'Araç seçin' }]}>
            <Select placeholder="Araç seçin">
              {vehicles.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {v.plate} {v.make && v.model ? `- ${v.make} ${v.model}` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="responsibleUserId" label="Sorumlu Personel">
            <Select placeholder="Seçin" allowClear>
              {users.map((u) => (
                <Select.Option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="complaintNotes" label="Şikayet/Not">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <AccountSelectModal
        visible={accountModalVisible}
        onClose={() => setAccountModalVisible(false)}
        onSelect={handleAccountSelect}
      />
    </div>
  );
};

export default Ajanda;
